import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import HotTable from "react-handsontable";
import axios from "axios";

import {
  getColumns,
  getColHeaders,
  getColumnWidth,
  generateData
} from "./data";

const AccessEditor = ({ customForms, preSelectedCustomForm, customFormUsersAccessData, locations, positions }) => {
  const hotRef = useRef(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedUserType, setSelectedUserType] = useState('');
  const [selectedCustomForm, setSelectedCustomForm] = useState(preSelectedCustomForm || '');

  const filterCustomFormsByLocation = (customForms, selectedLocation) => {
    if (selectedLocation === '') return customForms;

    let filteredCustomForms = _.filter(customForms, (customForm) => {
      if(!customForm.location_id) return true

      return customForm.location_id === parseInt(selectedLocation)
    })

    if(!selectedCustomForm) return filteredCustomForms
    if(_.map(filteredCustomForms, 'id').includes(parseInt(selectedCustomForm))) return filteredCustomForms

    setSelectedCustomForm('');
    return filteredCustomForms
  }
  const filterCustomFormsByCustomForm = (customForms, selectedCustomForm) => {
    if (selectedCustomForm === '') return customForms;

    return _.filter(customForms, {id: parseInt(selectedCustomForm)})
  }
  const filterUsersByLocation = (users, selectedLocation) => {
    if (selectedLocation === '') return users;

    return _.filter(users, (user) => {
      return user.location_ids.includes(parseInt(selectedLocation))
    })
  }
  const filterUsersByPosition = (users, selectedPosition) => {
    if (selectedPosition === '') return users;

    return _.filter(users, (user) => {
      return user.position_ids.includes(parseInt(selectedPosition))
    })
  }
  const filterUsersByUserType = (users, selectedUserType) => {
    if (selectedUserType === '') return users;

    return _.filter(users, {user_type: selectedUserType})
  }

  let filteredCustomFormsByLocation = filterCustomFormsByLocation(customForms, selectedLocation)
  let filteredCustomForms = filterCustomFormsByCustomForm(filteredCustomFormsByLocation, selectedCustomForm)

  let filteredCustomFormUsersAccessData = filterUsersByLocation(customFormUsersAccessData, selectedLocation)
  filteredCustomFormUsersAccessData = filterUsersByPosition(filteredCustomFormUsersAccessData, selectedPosition)
  filteredCustomFormUsersAccessData = filterUsersByUserType(filteredCustomFormUsersAccessData, selectedUserType)

  const onAfterChange = (changesArray, source) => {
    const hot = hotRef.current && hotRef.current.hotInstance;
    if (!((changesArray && (source === "edit" || source === "Autofill.fill")) || source === "CopyPaste.paste")) return;

    _.forEach(changesArray, (item) => {
      const [ row, col, oldValue, newValue ] = item;

      if(newValue == '') {
        hot.setDataAtCell(row, col, oldValue)
        return;
      }
    })

    let data = generateData(filteredCustomFormUsersAccessData, changesArray);
    if (data.length === 0) return;

    axios.put(
      `/custom_forms/apply_access.json`,
      { custom_form_users: data }
    ).then(response => {
      toastr.info(response.data.message);
    }).catch(() => {
      toastr.error("User access was not updated");
    })
  }

  return (
    <div>
      <h1>Custom Form Access Editor</h1>
      <div className="row">
        <div className="col-md-3 col-sm-6 col-xs-12">
          <select
            className="form-control"
            title="Select Location"
            value={selectedLocation}
            onChange={e => setSelectedLocation(e.target.value)}
          >
            <option value=''>All Locations</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 col-sm-6 col-xs-12">
          <select
            className="form-control"
            title="Select Position"
            value={selectedPosition}
            onChange={e => setSelectedPosition(e.target.value)}
          >
            <option value=''>All Positions</option>
            {positions.map(position => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 col-sm-6 col-xs-12">
          <select
            className="form-control"
            title="Select User Type"
            value={selectedUserType}
            onChange={e => setSelectedUserType(e.target.value)}
          >
            <option value=''>All Employees</option>
            <option value='non_admin'>Non-Admin</option>
            <option value='admin'>Admin</option>
            <option value='superadmin'>Super Admin</option>
          </select>
        </div>
        <div className="col-md-3 col-sm-6 col-xs-12">
          <select
            className="form-control"
            title="Select Custom Forms"
            value={selectedCustomForm}
            onChange={e => setSelectedCustomForm(e.target.value)}
          >
            <option value=''>All Custom Forms</option>
            {filteredCustomFormsByLocation.map(customForm => (
              <option key={customForm.id} value={customForm.id}>
                {customForm.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      <HotTable
        ref={hotRef}
        data={filteredCustomFormUsersAccessData}
        colWidths={getColumnWidth(filteredCustomForms)}
        rowHeaders={false}
        autoRowSize={true}
        colHeaders={getColHeaders(filteredCustomForms)}
        columns={getColumns(filteredCustomForms)}
        hiddenColumns={{columns: {data: 'user_id'}}}
        width={1200}
        height={500}
        onAfterChange={onAfterChange}
        fixedColumnsLeft={2}
        afterRender={() => {
          const copyPasteElement = document.getElementById("HandsontableCopyPaste");
          if (copyPasteElement) {
            copyPasteElement.setAttribute("aria-label", "Paste Area for Handsontable");
          }
          // Make scrollable containers focusable
          const scrollContainers = document.querySelectorAll('.ht_master .wtHolder, .ht_clone_left .wtHolder');
          scrollContainers.forEach(el => {
            el.setAttribute('tabindex', '0');
            el.setAttribute('role', 'region');
            el.setAttribute('aria-label', 'Scrollable table content');
          });
        }}
        cells={(row, col) => {
          const cellProperties = {};
          cellProperties.className = 'custom-cell';
          return cellProperties;
        }}
      />
    </div>
  )
}

AccessEditor.propTypes = {
  customForms: PropTypes.array,
  preSelectedCustomForm: PropTypes.number,
  customFormUsersAccessData: PropTypes.array,
  locations: PropTypes.array,
  positions: PropTypes.array
};

export default AccessEditor;
